import React, { useState, useEffect, useCallback } from "react";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
import moment from "moment";

import es from "date-fns/locale/es";
import {
  Picker,
  Container,
  ImgContainer,
  Icon,
  ReactToolTip,
  ToolTipImg,
  ToolTipImgContainer
} from "./Styles";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("es", es);

export const launchPicker = (element) => {
  if (!element) {
    return;
  }
  element.click();
};

const parsePickerValue = (value, parseStringValueAsLocalDate = false) => {
  if (!value) {
    return "";
  }

  if (parseStringValueAsLocalDate && typeof value === "string") {
    const yearMonthMatch = value.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);

    if (yearMonthMatch) {
      const [, year, month, day] = yearMonthMatch;
      return new Date(Number(year), Number(month) - 1, Number(day || 1));
    }
  }

  const parsedValue = value instanceof Date ? value : new Date(value);

  return Number.isNaN(parsedValue.getTime()) ? "" : parsedValue;
};

const DatePickerInput = (props) => {
  const {
    value,
    format,
    placeholder,
    onChange,
    monthDropdown,
    yearDropdown,
    showTooltip,
    error,
    className,
    maxDate,
    minDate,
    onBlur,
    setTouchedIcon,
    isSummary,
    isDisabled,
    errorMessage,
    customIcon,
    capitalizeDisplayValue,
    parseStringValueAsLocalDate,
    ...pickerProps
  } = props;
  const [startDate, setStartDate] = useState(
    parsePickerValue(value, parseStringValueAsLocalDate)
  );
  const [internalError, setInternalError] = useState(null);
  // Mostrar tooltip si hay error (externo o interno) y showTooltip está habilitado
  const finalError = error || internalError;
  const renderTooltip = showTooltip && finalError;
  const { t } = useTranslation();

  const validateDate = useCallback(
    (date) => {
      if (!date || date === "") {
        setInternalError(null);
        return true;
      }

      const selectedMoment = moment(date);

      // Validar maxDate
      if (maxDate && selectedMoment.isAfter(moment(maxDate), "day")) {
        setInternalError(t("datepicker.error.futureDate"));
        return false;
      }

      // Validar minDate
      if (minDate && selectedMoment.isBefore(moment(minDate), "day")) {
        setInternalError(t("datepicker.error.pastDate"));
        return false;
      }

      setInternalError(null);
      return true;
    },
    [maxDate, minDate, t]
  );

  // Validar fecha cuando cambia el valor o las restricciones
  useEffect(() => {
    if (startDate && startDate !== "") {
      validateDate(startDate);
    } else {
      setInternalError(null);
    }
  }, [startDate, validateDate]);

  useEffect(() => {
    setStartDate(parsePickerValue(value, parseStringValueAsLocalDate));
  }, [parseStringValueAsLocalDate, value]);

  const handleCalendarClose = () => {
    if (startDate === "") {
      setTouchedIcon(true);
    }
  };

  const handleCalendarOpen = () => {
    setTouchedIcon(false);
  };

  return (
    <Container className={className} isSummary={isSummary}>
      <Picker
        data-testid="datepicker-test"
        id="datepicker"
        isSummary={isSummary}
        isDisabled={isDisabled}
        $capitalizeDisplayValue={capitalizeDisplayValue}
        selected={startDate}
        locale="es"
        dateFormat={format}
        placeholderText={placeholder}
        showMonthDropdown={monthDropdown}
        showYearDropdown={yearDropdown}
        scrollableYearDropdown={true}
        maxDate={maxDate}
        dropdownMode="select"
        onChange={(date) => {
          onChange(date);
          setStartDate(date);
        }}
        onCalendarClose={handleCalendarClose}
        onCalendarOpen={handleCalendarOpen}
        {...pickerProps}
      />
      {renderTooltip && (
        <>
          <ToolTipImgContainer
            data-tip={true}
            data-for="datepicker-error-tooltip"
          >
            <ToolTipImg src="/assets/images/error.png" alt="error" />
          </ToolTipImgContainer>

          <ReactToolTip
            id="datepicker-error-tooltip"
            place="bottom"
            type="error"
            effect="solid"
          >
            <div className="error-tooltip__container">
              <p className="error-tooltip__text--title">
                {errorMessage || t(`${error}`) || "Selecciona una fecha válida"}
              </p>
            </div>
          </ReactToolTip>
        </>
      )}
      <ImgContainer
        isDisabled={isDisabled}
        onClick={() => {
          if (isDisabled) {
            return;
          }
          launchPicker(document.getElementById(props.id));
          onBlur();
        }}
        id="picker-icon"
      >
        {customIcon || (
          <Icon
            src={
              isSummary
                ? "/assets/images/imageCalendarSummary_.png"
                : "/assets/images/calendar_icon.png"
            }
            alt="Date picker"
            isSummary={isSummary}
            isDisabled={isDisabled}
          />
        )}
      </ImgContainer>
    </Container>
  );
};

DatePickerInput.propTypes = {
  value: propTypes.string,
  format: propTypes.string,
  error: propTypes.string,
  errorMessage: propTypes.string,
  showTooltip: propTypes.bool,
  placeholder: propTypes.string,
  monthDropdown: propTypes.bool,
  yearDropdown: propTypes.bool,
  onChange: propTypes.func.isRequired,
  id: propTypes.string,
  className: propTypes.string,
  maxDate: propTypes.instanceOf(Date),
  minDate: propTypes.instanceOf(Date),
  onBlur: propTypes.func,
  isSummary: propTypes.bool,
  isDisabled: propTypes.bool,
  setTouchedIcon: propTypes.func,
  customIcon: propTypes.node,
  capitalizeDisplayValue: propTypes.bool,
  parseStringValueAsLocalDate: propTypes.bool
};

DatePickerInput.defaultProps = {
  maxDate: undefined,
  minDate: undefined,
  value: "",
  className: "",
  format: "yyyy-MM-dd",
  placeholder: "",
  monthDropdown: true,
  yearDropdown: true,
  error: null,
  errorMessage: null,
  showTooltip: true,
  id: "",
  isSummary: false,
  isDisabled: false,
  onBlur: () => {},
  setTouchedIcon: () => {},
  customIcon: null,
  capitalizeDisplayValue: false,
  parseStringValueAsLocalDate: false
};

export default DatePickerInput;
